import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Sparkle, Check } from "@phosphor-icons/react";

const CLS = {
  Hot: "bg-red-50 text-red-700 border-red-200",
  Urgent: "bg-amber-50 text-amber-700 border-amber-200",
  Normal: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function AIPrioritySuggest({ candidateId, canApply, currentPriority, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/ai/candidate-priority/${candidateId}`);
      setData(r.data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      await api.post(`/ai/candidate-priority/${candidateId}/apply`, { priority: data.priority });
      toast.success(`Priority set to ${data.priority}`);
      onApplied?.(data.priority);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="ai-priority-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-violet-600 text-white grid place-items-center">
            <Sparkle size={16} weight="fill" />
          </div>
          <div>
            <h3 className="font-semibold">AI Priority Suggestion</h3>
            <p className="text-xs text-slate-500">GPT 5.2 · Based on activity signals</p>
          </div>
        </div>
        <button onClick={run} disabled={loading} data-testid="run-priority-btn"
                className="text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-md">
          {loading ? "Analysing…" : data ? "Re-analyse" : "Analyse"}
        </button>
      </div>

      {!data && !loading && (
        <p className="text-sm text-slate-500">Get an AI-recommended priority level with reasoning and next steps.</p>
      )}

      {data && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2.5 py-1 rounded-full border ${CLS[data.priority] || CLS.Normal}`}>
              Recommended · {data.priority}
            </span>
            <span className="text-xs text-slate-500">Confidence {data.confidence}%</span>
            {canApply && data.priority !== currentPriority && (
              <button onClick={apply} disabled={applying} data-testid="apply-priority-btn"
                      className="ml-auto inline-flex items-center gap-1 text-xs border border-slate-200 hover:bg-slate-50 rounded-md px-2 py-1">
                <Check size={12} /> Apply
              </button>
            )}
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{data.reason}</p>
          {data.actions?.length > 0 && (
            <ul className="space-y-1">
              {data.actions.map((a) => (
                <li key={a} className="text-sm text-slate-700 flex gap-2">
                  <span className="w-1 h-1 rounded-full bg-violet-500 mt-2 shrink-0" /> {a}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
